import produce from "immer";
import { AxiosResponse } from "axios";
import CustomError from "../lib/api/CustomError";

// Action types
const FETCH_TASK = "FETCH_TASK";
const UPDATE_TITLE = "UPDATE_TITLE";
const FORM_SUBMITTING = "FORM_SUBMITTING";
const TASK_READY = "TASK_READY";
const JOB_DONE = "JOB_DONE";
const ERROR_ACTION = "ERROR_ACTION";
const VALIDATION_ERRORS = "VALIDATION_ERRORS";
const CLEAR_VALIDATION_ERRORS = "CLEAR_VALIDATION_ERRORS";
const REMOVE_FORM = "REMOVE_FORM";

interface FetchTaskAction {
	type: typeof FETCH_TASK;
	data: ReturnType<typeof handleTaskResponse>;
}

interface UpdateTitleAction {
	type: typeof UPDATE_TITLE;
	data: string;
}

interface TaskReadyAction {
	type: typeof TASK_READY;
}

interface FormSubmittingAction {
	type: typeof FORM_SUBMITTING;
}

interface JobDoneAction {
	type: typeof JOB_DONE;
}

interface ErrorAction {
	type: typeof ERROR_ACTION;
	error: CustomError;
}

interface ValidationErrorAction {
	type: typeof VALIDATION_ERRORS;
}

interface ClearValidationErrorsAction {
	type: typeof CLEAR_VALIDATION_ERRORS;
}

interface RemoveFormAction {
	type: typeof REMOVE_FORM;
}

type TaskActions =
	| FetchTaskAction
	| UpdateTitleAction
	| TaskReadyAction
	| JobDoneAction
	| RemoveFormAction
	| FormSubmittingAction
	| ValidationErrorAction
	| ClearValidationErrorsAction
	| ErrorAction;

export interface TaskState {
	saving: boolean;
	submitting: boolean;
	ready: boolean;
	id?: number;
	fid?: string;
	title?: string;
	canBeUnassigned?: boolean;
	canBeReassigned?: boolean;
	done: boolean;
	error?: CustomError;
	validationErrors: boolean;
	canDeleteActivity?: boolean;
	assignee?: string;
	startDate?: string;
	dueDate?: string;
	canSaveTask?: boolean;
}

export const defaultState = (props: Partial<TaskState> = {}): TaskState => ({
	saving: false,
	submitting: false,
	ready: false,
	done: false,
	validationErrors: false,
	...props,
});

export default (
	state: TaskState = defaultState(),
	action: TaskActions
): TaskState => {
	return produce(state, draft => {
		switch (action.type) {
			case FETCH_TASK:
				return Object.assign({}, state, action.data, { ready: true });

			case UPDATE_TITLE:
				draft.title = action.data;
				break;

			case TASK_READY:
				draft.submitting = false;
				draft.saving = false;
				draft.ready = true;
				break;

			case FORM_SUBMITTING:
				draft.ready = false;
				draft.submitting = true;
				break;

			case CLEAR_VALIDATION_ERRORS:
				draft.validationErrors = false;
				break;

			case VALIDATION_ERRORS:
				draft.validationErrors = true;
				break;

			case ERROR_ACTION:
				draft.error = action.error;
				break;

			case JOB_DONE:
				draft.done = true;
				break;

			case REMOVE_FORM:
				draft.fid = undefined;
				break;

			default:
				return state;
		}
	});
};

// Action creators
export const fetchAction = (
	data: ReturnType<typeof handleTaskResponse>
): FetchTaskAction => ({
	type: FETCH_TASK,
	data,
});

export function handleTaskResponse(task: AxiosResponse) {
	const data = task.data.data[0];
	return {
		assignee: data.assignee,
		startDate: data.createdTime,
		dueDate: data.taskDueDate,
		title: data.taskName,
		id: data.id,
		fid: data.formInstanceId,
		canBeUnassigned: data.canBeUnassigned,
		canBeReassigned: data.canBeReassigned,
		canDeleteActivity: data.canDeleteActivity,
		canSaveTask: data.canSave,
		activityStarter: data.activityStarter,
	};
}

export const updateTitleAction = (data: string): UpdateTitleAction => ({
	type: UPDATE_TITLE,
	data,
});

export const removeFormFromTask = (): RemoveFormAction => ({
	type: REMOVE_FORM,
});

export const taskReadyAction = (): TaskReadyAction => ({ type: TASK_READY });

export const errorAction = (error: CustomError): ErrorAction => ({
	type: ERROR_ACTION,
	error,
});
export const jobDoneAction = (): JobDoneAction => ({ type: JOB_DONE });

export const formSubmittingAction = (): FormSubmittingAction => ({
	type: FORM_SUBMITTING,
});
export const validationErrorAction = (): ValidationErrorAction => ({
	type: VALIDATION_ERRORS,
});

export const clearValidationErrorsAction = (): ClearValidationErrorsAction => ({
	type: CLEAR_VALIDATION_ERRORS,
});
